if (!customElements.get('tcc-marquee')) {
  class TccMarquee extends HTMLElement {
    constructor() {
      super();
      this.rebuild = this.rebuild.bind(this);
      this.resizeObserver = null;
      this.isInitialised = false;
    }

    connectedCallback() {
      // Wait for DOM layout to settle and styles/fonts to render
      requestAnimationFrame(() => {
        this.initMarquee();
      });

      // Handle window resize via ResizeObserver for responsive recalculation
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.isInitialised) {
            this.rebuild();
          }
        });
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.rebuild);
      }

      // Handle Shopify editor live preview updates
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target && (e.target.contains(this) || e.target === this)) {
          this.initMarquee();
        }
      });

      // Recalculate once custom icons or images inside items finish loading
      const images = this.querySelectorAll('img');
      images.forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', () => this.rebuild(), { once: true });
        }
      });
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this.rebuild);
      }
    }

    initMarquee() {
      this.wrapper = this.querySelector('.tcc-marquee__wrapper');
      this.track = this.querySelector('.tcc-marquee__track');
      this.groups = this.querySelectorAll('.tcc-marquee__group');

      if (!this.wrapper || !this.track || this.groups.length < 2) return;

      this.group1 = this.groups[0];
      this.group2 = this.groups[1];

      // Save original HTML template of a single group
      this.originalHtml = this.group1.innerHTML;

      // Retrieve configured base speed from data attribute or CSS custom property
      const attrSpeed = parseFloat(this.dataset.speed);
      const computedSpeedStr = getComputedStyle(this).getPropertyValue('--tcc-marquee-speed').trim();
      this.baseSpeed = !isNaN(attrSpeed) && attrSpeed > 0 ? attrSpeed : (parseFloat(computedSpeedStr) || 25);

      this.rebuild();
      this.isInitialised = true;
    }

    rebuild() {
      if (!this.group1 || !this.originalHtml || !this.wrapper) return;

      const containerWidth = this.wrapper.clientWidth || this.clientWidth || window.innerWidth;
      if (containerWidth === 0) return;

      // Temporarily reset group1 to original content to accurately measure single-set width
      this.group1.innerHTML = this.originalHtml;
      const singleSetWidth = this.group1.getBoundingClientRect().width;

      if (singleSetWidth <= 0) return;

      // Ensure group width is at least 120% of container width so the track is always filled
      const targetGroupWidth = containerWidth * 1.2;
      const repeatCount = Math.max(1, Math.ceil(targetGroupWidth / singleSetWidth));

      // Build repeated HTML for seamless seamless continuous loop
      let repeatedHtml = '';
      for (let i = 0; i < repeatCount; i++) {
        repeatedHtml += this.originalHtml;
      }
      this.group1.innerHTML = repeatedHtml;

      // Ensure group2 is an exact duplicate of group1
      this.group2.innerHTML = repeatedHtml;

      // Scale animation speed proportionally to keep scroll velocity visually constant
      const newGroupWidth = this.group1.getBoundingClientRect().width;
      const speedMultiplier = newGroupWidth / singleSetWidth;
      const adjustedSpeed = (this.baseSpeed * speedMultiplier).toFixed(2);

      this.style.setProperty('--tcc-marquee-speed', `${adjustedSpeed}s`);
    }
  }

  customElements.define('tcc-marquee', TccMarquee);
}
